import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  getHubLiveConnectionDetail,
  hubLiveConnectionBannerMessage,
  onHubLiveConnectionChange,
  type HubLiveConnectionDetail,
} from "@/lib/hubLiveConnection";

/** Delay before showing a transient reconnecting state (avoids flicker on clean SSE recycle). */
const RECONNECTING_BANNER_DELAY_MS = 2_500;

/**
 * Top-of-screen notice when the hub presence live SSE cannot reconnect.
 * Presence and other live updates depend on that stream; polling is not a substitute.
 */
export function HubLiveConnectionBanner() {
  const [detail, setDetail] = useState<HubLiveConnectionDetail>(() => getHubLiveConnectionDetail());
  const [showReconnecting, setShowReconnecting] = useState(false);

  useEffect(() => onHubLiveConnectionChange(setDetail), []);

  useEffect(() => {
    if (detail.status !== "reconnecting") {
      setShowReconnecting(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowReconnecting(true);
    }, RECONNECTING_BANNER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [detail.status]);

  const message =
    detail.status === "reconnecting" && !showReconnecting
      ? null
      : hubLiveConnectionBannerMessage(detail);

  if (message == null) {
    return null;
  }

  const isDisconnected = detail.status === "disconnected";
  const tone = isDisconnected
    ? {
        border: "border-red-200",
        bg: "bg-red-50",
        text: "text-red-950",
        icon: "text-red-700",
      }
    : {
        border: "border-amber-200",
        bg: "bg-amber-50",
        text: "text-amber-950",
        icon: "text-amber-700",
      };

  return (
    <div
      role={isDisconnected ? "alert" : "status"}
      aria-live={isDisconnected ? "assertive" : "polite"}
      className="fixed inset-x-0 top-0 z-[310] flex justify-center px-4 pt-3"
    >
      <div
        className={`flex max-w-3xl flex-wrap items-center gap-3 rounded-md border ${tone.border} ${tone.bg} px-4 py-2 text-sm ${tone.text} shadow-sm`}
      >
        <WifiOff className={`h-4 w-4 shrink-0 ${tone.icon}`} aria-hidden="true" />
        <span className="min-w-0 flex-1">{message}</span>
        {isDisconnected ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload
          </Button>
        ) : null}
      </div>
    </div>
  );
}
